/**
 * GmSidebar — Persistent left navigation for the GM Dashboard.
 *
 * Mirrors MLBSidebar's NavLink + isActive pattern, but drops the team-primary
 * accent in favor of SLAM gold (#FFD700) since the GM surface is the
 * cross-team / front-office command shell, not a single team's view.
 *
 * Top: SLAM SPORTS wordmark + JS user-initial circle + "SLAM SPORTS AI"
 *      pill. (No image asset — basketball uses /slam-logo.png; we don't
 *      ship one yet.)
 * Body: NAV_SECTIONS, mirrored from the basketball Coach Dashboard but
 *      reskinned for baseball (Roster & Stats, Trade Block, Payroll & Arb,
 *      Front Office Ethos, etc.). Exported so route registries can
 *      enumerate it.
 * Footer: "James Schlauch" account chip with "ADMIN · —" badge.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  UserPlus,
  ListTodo,
  Repeat,
  DollarSign,
  Film,
  CalendarSearch,
  Users,
  BarChart3,
  Settings,
  Search,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

const GOLD = '#FED603';
const ACTIVE_BG = 'rgba(254, 214, 3, 0.08)';

/* -----------------------------------------------------------------------------
 * Icon registry — items in NAV_SECTIONS reference icons by string name so the
 * structure stays serializable / export-friendly. Resolve to component here.
 * --------------------------------------------------------------------------- */
const ICON_MAP = {
  LayoutDashboard,
  ClipboardList,
  UserPlus,
  ListTodo,
  Repeat,
  DollarSign,
  Film,
  CalendarSearch,
  Users,
  BarChart3,
  Settings,
  Search,
  Sparkles,
};

// Team Tasks is a toggleable demo surface (FB-2026-05-15-01). Keep the nav
// entry in lockstep with the route guard in App.js so hiding the feature
// hides both the page and its sidebar link.
const TEAM_TASKS_ENABLED = process.env.REACT_APP_FEATURE_TEAM_TASKS !== 'false';

export const NAV_SECTIONS = [
  {
    label: 'DASHBOARD',
    items: [
      { label: 'Dashboard', icon: 'LayoutDashboard', to: '/gm/dashboard' },
    ],
  },
  {
    label: 'TEAM',
    items: [
      { label: '40-Man Roster', icon: 'ClipboardList', to: '/gm/roster' },
      ...(TEAM_TASKS_ENABLED ? [{ label: 'Team Tasks', icon: 'ListTodo', to: '/gm/tasks' }] : []),
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      // FB-2026-05-20-07: Advance Scouting was duplicated here and in MLB SUITE.
      // Both pointed at the same flattened page (/mlb/advance-scouting), so the
      // INTELLIGENCE duplicate is removed — the canonical entry lives in MLB SUITE.
      // FB-2026-05-20-06: 'CBA Trade Machine' → 'Trade Analysis'. The page is now an
      // Ask Slam AI chat (GmTradeBlockPage), not the retired CBA trade simulator.
      { label: 'Trade Analysis', icon: 'Repeat', to: '/gm/trade-block' },
      { label: 'Film Room', icon: 'Film', to: '/gm/film-room' },
    ],
  },
  {
    label: 'MLB SUITE',
    items: [
      { label: 'Overview', icon: 'BarChart3', to: '/mlb/overview' },
      { label: 'Roster & Evaluations', icon: 'ClipboardList', to: '/mlb/roster' },
      { label: 'Advanced Scouting', icon: 'Search', to: '/mlb/advance-scouting' },
      { label: 'Diamond Eye', icon: 'Film', to: '/mlb/diamond-eye' },
      { label: 'Ask SLAM AI', icon: 'Sparkles', to: '/mlb/ask-ai' },
    ],
  },
  {
    label: 'PROGRAM',
    items: [
      { label: 'Administration', icon: 'Users', to: '/gm/admin' },
      { label: 'Usage', icon: 'BarChart3', to: '/gm/usage' },
      { label: 'Front Office Ethos', icon: 'Settings', to: '/gm/ethos' },
    ],
  },
];

const sectionHeadingStyle = {
  padding: '0 20px 8px',
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'rgba(223, 226, 235, 0.72)',
  fontFamily: 'var(--font-family-headline, Montserrat, sans-serif)',
};

export default function GmSidebar({
  collapsed = false,
  onToggleCollapse,
  isMobile = false,
  onNavItemClick,
}) {
  const handleItemClick = () => {
    if (onNavItemClick) onNavItemClick();
  };

  const renderNavItem = (item) => {
    const Icon = ICON_MAP[item.icon];
    if (!Icon) return null;

    return (
      <NavLink
        key={item.to}
        to={item.to}
        title={collapsed ? item.label : undefined}
        onClick={handleItemClick}
        style={({ isActive }) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? '0' : '10px',
          width: '100%',
          padding: collapsed ? '8px 0' : '8px 20px',
          textDecoration: 'none',
          background: isActive ? ACTIVE_BG : 'transparent',
          borderLeft: isActive ? `2px solid ${GOLD}` : '2px solid transparent',
          color: isActive ? GOLD : 'var(--text-tertiary, #9ea6b4)',
          fontSize: '0.8125rem',
          fontWeight: 500,
          fontFamily: 'var(--font-family-body, "Open Sans", sans-serif)',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'all 150ms ease',
          boxSizing: 'border-box',
        })}
        onMouseEnter={(e) => {
          if (e.currentTarget.getAttribute('aria-current') !== 'page') {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
            e.currentTarget.style.color = 'var(--text-primary, #dfe2eb)';
          }
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget.getAttribute('aria-current') !== 'page') {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-tertiary, #9ea6b4)';
          }
        }}
      >
        <Icon size={16} strokeWidth={1.5} />
        {!collapsed && item.label}
      </NavLink>
    );
  };

  return (
    <nav
      data-testid="gm-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        // Desktop: in-flow, lets flex parent reserve main-column space.
        // Mobile: fixed overlay drawer (parent renders backdrop).
        position: isMobile ? 'fixed' : 'relative',
        top: isMobile ? 0 : undefined,
        left: isMobile ? 0 : undefined,
        flex: isMobile ? undefined : '0 0 auto',
        width: collapsed ? '64px' : 'var(--sidebar-width, 240px)',
        height: isMobile ? '100vh' : 'auto',
        alignSelf: isMobile ? undefined : 'stretch',
        background: 'var(--bg-surface-0, #0a0e14)',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 0 16px',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: isMobile ? 50 : 'var(--z-fixed, 30)',
        transition: 'width 250ms ease',
        boxSizing: 'border-box',
      }}
    >
      {/* Header — wordmark + initial + PROD pill */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: collapsed ? 'center' : 'flex-start',
          gap: '8px',
          padding: collapsed ? '0 8px 12px' : '0 20px 12px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          {!collapsed && (
            <div
              style={{
                fontFamily: 'var(--font-family-headline, Montserrat, sans-serif)',
                color: GOLD,
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: '0.06em',
                lineHeight: 1.1,
              }}
            >
              SLAM SPORTS
            </div>
          )}
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              padding: 0,
              background: 'transparent',
              border: 'none',
              color: 'var(--text-quaternary, #858d9c)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary, #999077)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-quaternary, #858d9c)'; }}
          >
            {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          </button>
        </div>

        {/* JS initial circle (placeholder for future logo image) */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '9999px',
            background: 'rgba(254, 214, 3, 0.12)',
            color: GOLD,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          JS
        </div>

        {!collapsed && (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '9999px',
              background: GOLD,
              color: '#000',
              fontSize: '0.625rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontFamily: 'var(--font-family-headline, Montserrat, sans-serif)',
            }}
          >
            SLAM SPORTS AI
          </span>
        )}
      </div>

      {/* Navigation sections */}
      <div data-guide="sidebar-nav" style={{ flex: 1 }}>
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} style={{ marginBottom: '20px' }}>
            {!collapsed && (
              <div style={sectionHeadingStyle}>{section.label}</div>
            )}
            {section.items.map(renderNavItem)}
          </div>
        ))}
      </div>

      {/* Footer — account chip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: collapsed ? '12px 8px 0' : '12px 20px 0',
          borderTop: '1px solid rgba(255, 255, 255, 0.04)',
          marginTop: '8px',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            flexShrink: 0,
            borderRadius: '9999px',
            background: 'rgba(254, 214, 3, 0.12)',
            color: GOLD,
            fontSize: '0.8125rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          JS
        </div>

        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <span
              style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--text-primary, #dfe2eb)',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              James Schlauch
            </span>
            <span
              style={{
                display: 'inline-block',
                alignSelf: 'flex-start',
                fontSize: '0.5625rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: GOLD,
                background: 'rgba(254, 214, 3, 0.08)',
                border: '1px solid rgba(254, 214, 3, 0.2)',
                borderRadius: '9999px',
                padding: '2px 8px',
              }}
            >
              ADMIN &middot; &mdash;
            </span>
          </div>
        )}
      </div>
    </nav>
  );
}
